import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Send, Loader2, BookOpen, Image, MessageCircle, PenLine, Sparkles,
  Clock, ChevronDown, ChevronUp, Upload, Trash2, FileText, Plus, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════
// MEMORY BOOK
// ═══════════════════════════════════════════════════════
function MemoryBook({ patients }) {
  const [selectedPatient, setSelectedPatient] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [memoryType, setMemoryType] = useState('story');
  const [memoryDate, setMemoryDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);

  const typeStyles = {
    story: { label: 'Story', bg: 'bg-terra-50', text: 'text-terra-700', border: 'border-terra-200' },
    milestone: { label: 'Milestone', bg: 'bg-sage-50', text: 'text-sage-700', border: 'border-sage-200' },
    note: { label: 'Note', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200' },
  };

  const loadEntries = async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/wave2/memory-book/${pid}`);
      const data = await res.json();
      setEntries(data.entries || []);
    } catch { setEntries([]); }
    setLoading(false);
  };

  useEffect(() => { if (selectedPatient) loadEntries(selectedPatient); }, [selectedPatient]);

  const saveEntry = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/wave2/memory-book/entry`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: selectedPatient, title, content,
          memory_type: memoryType, date_of_memory: memoryDate || null
        }),
      });
      const newEntry = await res.json();
      setEntries(prev => [newEntry, ...prev]);
      setTitle(''); setContent(''); setMemoryDate(''); setShowForm(false);
    } catch {}
    setSaving(false);
  };

  const uploadPhoto = async (entryId, file) => {
    setUploading(entryId);
    const formData = new FormData();
    formData.append('patient_id', selectedPatient);
    formData.append('memory_id', entryId);
    formData.append('caption', '');
    formData.append('file', file);
    try {
      await fetch(`${API}/api/wave2/memory-book/upload-photo`, { method: 'POST', body: formData });
      loadEntries(selectedPatient);
    } catch {}
    setUploading(null);
  };

  const deleteEntry = async (entryId) => {
    try {
      await fetch(`${API}/api/wave2/memory-book/entry/${entryId}`, { method: 'DELETE' });
      setEntries(prev => prev.filter(e => e.id !== entryId));
    } catch {}
  };

  return (
    <div data-testid="memory-book">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="w-5 h-5 text-terra-400" />
        <h2 className="text-lg font-heading font-bold text-[#2C3D30]">Memory Book</h2>
        <Badge className="bg-terra-50 text-terra-400 text-xs">Digital Legacy</Badge>
      </div>

      <select value={selectedPatient} onChange={e => setSelectedPatient(e.target.value)}
        className="w-full rounded-xl border border-sand-200 px-3 py-2 text-sm bg-sand-50 mb-4"
        data-testid="mb-patient-select">
        <option value="">Select patient...</option>
        {patients.map(p => (
          <option key={p.id} value={p.id}>{p.name} — {p.primary_diagnosis || 'N/A'}</option>
        ))}
      </select>

      {selectedPatient && (
        <>
          {!showForm ? (
            <Button onClick={() => setShowForm(true)} className="w-full mb-4 bg-terra-400 hover:bg-terra-500"
              data-testid="mb-new-entry-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add a Memory
            </Button>
          ) : (
            <Card className="mb-4 border-terra-200" data-testid="mb-entry-form">
              <CardContent className="pt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-[#2C3D30]">New Memory</span>
                  <button onClick={() => setShowForm(false)} className="text-sand-400 hover:text-[#5C6A5F]">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-2">
                  {Object.entries(typeStyles).map(([key, style]) => (
                    <button key={key} onClick={() => setMemoryType(key)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        memoryType === key ? `${style.bg} ${style.text} border ${style.border}` : 'bg-sand-200 text-[#5C6A5F]'
                      }`} data-testid={`mb-type-${key}`}>
                      {style.label}
                    </button>
                  ))}
                </div>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title (e.g., 'The Day We Met')"
                  data-testid="mb-title-input" />
                <textarea value={content} onChange={e => setContent(e.target.value)}
                  placeholder="Share the memory..."
                  className="w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm min-h-[100px] resize-none"
                  data-testid="mb-content-input" />
                <Input type="date" value={memoryDate} onChange={e => setMemoryDate(e.target.value)}
                  data-testid="mb-date-input" />
                <Button onClick={saveEntry} disabled={saving || !title.trim()} className="w-full bg-terra-400 hover:bg-terra-500"
                  data-testid="mb-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <PenLine className="w-4 h-4 mr-1.5" />}
                  Save Memory
                </Button>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-terra-400 mr-2" />
              <span className="text-sm text-[#5C6A5F]">Loading memories...</span>
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-sand-400">
              <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No memories yet. Start capturing your loved one's story.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="mb-entries-list">
              {entries.filter(e => !e.is_deleted).map(entry => {
                const style = typeStyles[entry.memory_type] || typeStyles.note;
                return (
                  <Card key={entry.id} className={`border-l-4 ${style.border}`} data-testid={`mb-entry-${entry.id}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-sm font-semibold text-[#2C3D30]">{entry.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`text-xs ${style.bg} ${style.text}`}>{style.label}</Badge>
                            {entry.date_of_memory && (
                              <span className="text-xs text-sand-400">{entry.date_of_memory}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => fileInputRef.current?.click()}
                            className="p-1.5 rounded-md hover:bg-sand-200 text-sand-400 hover:text-terra-400"
                            title="Add photo" data-testid={`mb-upload-${entry.id}`}>
                            {uploading === entry.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Image className="w-3.5 h-3.5" />}
                          </button>
                          <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                            onChange={e => { if (e.target.files[0]) uploadPhoto(entry.id, e.target.files[0]); e.target.value=''; }} />
                          <button onClick={() => deleteEntry(entry.id)}
                            className="p-1.5 rounded-md hover:bg-terra-50 text-sand-400 hover:text-terra-400"
                            data-testid={`mb-delete-${entry.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      {entry.content && (
                        <p className="text-sm text-[#5C6A5F] leading-relaxed whitespace-pre-wrap mt-2">{entry.content}</p>
                      )}
                      {entry.media && entry.media.length > 0 && (
                        <div className="flex gap-2 mt-3 flex-wrap">
                          {entry.media.map(m => (
                            <div key={m.id} className="relative w-20 h-20 rounded-xl overflow-hidden border border-sand-200">
                              <img src={`${API}/api/wave2/memory-book/photo/${m.storage_path}`}
                                alt={m.caption || 'Memory'} className="w-full h-full object-cover"
                                onError={e => { e.target.style.display='none'; }} />
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-sand-400 mt-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(entry.created_at).toLocaleDateString()}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// LIFE STORY COMPANION
// ═══════════════════════════════════════════════════════
function LifeStoryCompanion({ patients }) {
  const [selectedPatient, setSelectedPatient] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [story, setStory] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startNewSession = () => {
    const newId = `ls-${Date.now()}`;
    setSessionId(newId);
    setMessages([]);
    setStory(null);
  };

  const loadSessions = async (pid) => {
    try {
      const res = await fetch(`${API}/api/wave2/life-story/sessions/${pid}`);
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch { setSessions([]); }
  };

  const loadSession = async (sid) => {
    try {
      const res = await fetch(`${API}/api/wave2/life-story/session/${sid}`);
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages(data.messages || []);
      setStory(null);
      setShowSessions(false);
    } catch {}
  };

  useEffect(() => {
    if (selectedPatient) {
      loadSessions(selectedPatient);
      startNewSession();
    }
  }, [selectedPatient]);

  const sendMessage = async () => {
    if (!input.trim() || !selectedPatient || !sessionId) return;
    const userMsg = input;
    setMessages(prev => [...prev, { role: 'user', text: userMsg, timestamp: new Date().toISOString() }]);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/wave2/life-story/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: selectedPatient, session_id: sessionId, message: userMsg }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', text: data.response, timestamp: new Date().toISOString() }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'I had trouble connecting. Please try again.', timestamp: new Date().toISOString() }]);
    }
    setLoading(false);
  };

  const generateStory = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/wave2/life-story/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: selectedPatient, session_id: sessionId }),
      });
      if (!res.ok) {
        const err = await res.json();
        setStory({ error: err.detail || 'Could not generate story yet.' });
      } else {
        setStory(await res.json());
      }
    } catch {
      setStory({ error: 'Unable to generate. Please try again.' });
    }
    setGenerating(false);
  };

  return (
    <div data-testid="life-story-companion">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-sky-300" />
        <h2 className="text-lg font-heading font-bold text-[#2C3D30]">Life Story Companion</h2>
        <Badge className="bg-sky-50 text-sky-500 text-xs">AI Interview</Badge>
      </div>

      <div className="flex gap-2 mb-4">
        <select value={selectedPatient} onChange={e => setSelectedPatient(e.target.value)}
          className="flex-1 rounded-xl border border-sand-200 px-3 py-2 text-sm bg-sand-50"
          data-testid="ls-patient-select">
          <option value="">Select patient...</option>
          {patients.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {selectedPatient && (
          <Button variant="outline" onClick={() => setShowSessions(!showSessions)} className="text-xs"
            data-testid="ls-sessions-toggle">
            <FileText className="w-3.5 h-3.5 mr-1" />
            {sessions.length} session{sessions.length !== 1 ? 's' : ''}
          </Button>
        )}
      </div>

      {showSessions && sessions.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="ls-sessions-list">
          {sessions.map(s => (
            <button key={s.session_id} onClick={() => loadSession(s.session_id)}
              className="w-full text-left p-3 rounded-xl border border-sand-200 hover:border-sky-300 hover:bg-sky-50 transition-colors">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-[#2C3D30]">{s.patient_name}</span>
                <Badge className={s.status === 'story_generated' ? 'bg-sage-50 text-sage-400' : 'bg-sand-200 text-[#5C6A5F]'} variant="outline">
                  {s.status === 'story_generated' ? 'Story Ready' : 'In Progress'}
                </Badge>
              </div>
              <p className="text-xs text-sand-400 mt-1">{new Date(s.created_at).toLocaleDateString()}</p>
            </button>
          ))}
          <Button variant="outline" onClick={startNewSession} className="w-full text-xs" data-testid="ls-new-session-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Start New Interview
          </Button>
        </div>
      )}

      {selectedPatient && sessionId && (
        <>
          <div className="bg-sky-50 border border-sky-100 rounded-xl p-4 mb-4 min-h-[250px] max-h-[400px] overflow-y-auto">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Sparkles className="w-6 h-6 mx-auto mb-2 text-sky-300" />
                <p className="text-sm text-sky-400 font-medium">Ready to begin</p>
                <p className="text-xs text-sky-300 mt-1">Share anything about your loved one to start the interview</p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} mb-3`}>
                <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm ${
                  msg.role === 'user'
                    ? 'bg-sky-600 text-white'
                    : 'bg-sand-50 text-[#2C3D30] border border-sky-100'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.text}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start mb-3">
                <div className="bg-sand-50 rounded-xl px-4 py-3 flex items-center gap-2 border border-sky-100">
                  <Loader2 className="w-4 h-4 animate-spin text-sky-300" />
                  <span className="text-sm text-sky-300">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={scrollRef} />
          </div>

          <div className="flex gap-2 mb-3">
            <Input value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage()}
              placeholder="Share a memory, answer a question..."
              className="flex-1" disabled={loading} data-testid="ls-message-input" />
            <Button onClick={sendMessage} disabled={loading || !input.trim()} data-testid="ls-send-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>

          {messages.length >= 4 && (
            <Button onClick={generateStory} disabled={generating}
              className="w-full bg-sky-300 hover:bg-sky-400" data-testid="ls-generate-story-btn">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Crafting the story...</>
                : <><FileText className="w-4 h-4 mr-1.5" />Generate Life Story</>}
            </Button>
          )}

          {story && !story.error && (
            <Card className="mt-4 border-l-4 border-l-sky-300" data-testid="ls-generated-story">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-[#2C3D30]">{story.patient_name}'s Life Story</span>
                  <Badge className="bg-sky-50 text-sky-500 text-xs">{story.word_count} words</Badge>
                </div>
                <div className="prose prose-sm max-w-none">
                  <p className="text-sm text-[#2C3D30] leading-relaxed whitespace-pre-wrap">{story.story_text}</p>
                </div>
                <p className="text-xs text-sand-400 mt-3 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Generated {new Date(story.generated_at).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          )}

          {story && story.error && (
            <div className="mt-4 p-3 rounded-xl bg-terra-50 border border-terra-100">
              <p className="text-sm text-terra-400">{story.error}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════
export default function Wave2FeaturesPage() {
  const [patients, setPatients] = useState([]);
  const [activeTab, setActiveTab] = useState('memory');

  useEffect(() => {
    fetch(`${API}/api/wave2/patients-list`)
      .then(r => r.ok ? r.json() : { patients: [] })
      .then(d => setPatients(d.patients || []))
      .catch(() => {});
  }, []);

  const tabs = [
    { id: 'memory', label: 'Memory Book', icon: BookOpen, activeClass: 'bg-terra-50 text-terra-400 border border-terra-200' },
    { id: 'lifestory', label: 'Life Story', icon: Sparkles, activeClass: 'bg-sky-50 text-sky-500 border border-sky-200' },
  ];

  return (
    <div className="min-h-screen bg-sand-100" data-testid="wave2-page">
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-heading font-bold text-[#2C3D30]">Digital Legacy</h1>
          <p className="text-sm text-[#5C6A5F] mt-1">Preserve memories and capture life stories for the ones you love</p>
        </div>

        <div className="flex gap-2 mb-6 border-b border-sand-200 pb-3">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeTab === tab.id ? tab.activeClass : 'text-[#5C6A5F] hover:text-[#2C3D30] hover:bg-sand-100'
              }`}
              data-testid={`wave2-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <Card className="shadow-sm">
          <CardContent className="p-6">
            {activeTab === 'memory' && <MemoryBook patients={patients} />}
            {activeTab === 'lifestory' && <LifeStoryCompanion patients={patients} />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
